/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import {
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useBreakpoints from '../../../common/hooks/useBreakpoints';
import Button from '../../../common/widgets/Button';
import ClickOutside from '../../../common/widgets/ClickOutside';
import Icons from '../../../common/widgets/Icons';
import { config } from '../config';
import { CustomerInfoContext } from '../contexts/CustomerInfoContext';
import {
  getAutocompleteSuggestionNavigationTarget,
  getAutocompleteSuggestionsCount,
  getAutocompleteSuggestionTitle,
  hasAutocompleteSuggestions,
} from '../helpers/autocompleteHelper';
import {
  AutocompleteResponseModel,
  AutocompleteSuggestionItem,
} from '../models/Search';
import { resources } from '../resources/resources';
import { searchProductService } from '../services/products/search';
import SearchSugestions from './SearchSugestions';

type LoadingState = { sugestionsLoading: boolean; isLoading: boolean };
type SearchState = {
  sugestions: AutocompleteResponseModel | null;
  searchValue: string;
  debounceSearchValue: string;
  showSearchValue: string;
  areSuggestionActive: boolean;
  showNoResultMessage: boolean;
};

const Search = () => {
  const [searchParams] = useSearchParams();
  const decodedInitial = searchParams.get('searchTerm') ?? '';
  const { customerInfo } = useContext(CustomerInfoContext);
  const navigate = useNavigate();
  const suggestionsRef = useRef<AutocompleteResponseModel | undefined>(
    undefined,
  );
  const autocompleteRef = useRef<HTMLDivElement | null>(null);
  const clipboardValueRef = useRef<string>();

  const { isTablet } = useBreakpoints();
  const { t: translate } = useTranslation();

  const [isLoadingState, setIsLoadingState] = useState<LoadingState>({
    sugestionsLoading: false,
    isLoading: false,
  });
  const [searchState, setSearchState] = useState<SearchState>({
    sugestions: null,
    searchValue: decodedInitial,
    debounceSearchValue: decodedInitial,
    showSearchValue: decodedInitial,
    areSuggestionActive: false,
    showNoResultMessage: false,
  });
  let lastSuggestionsSentTime = 0;
  const debounceSearchChange = _.debounce(
    async (abortController: AbortController) => {
      if (!searchState.areSuggestionActive) {
        setSearchState(
          produce(d => {
            d.areSuggestionActive = true;
          }),
        );
      }
      setIsLoadingState(
        produce(d => {
          d.sugestionsLoading = true;
        }),
      );
      (async () => {
        const result = await searchProductService.getAutocompleteV2(
          encodeURIComponent(searchState.debounceSearchValue),
          {
            signal: abortController?.signal,
          },
        );

        if (!result.hasErrors) {
          setSearchState(
            produce(d => {
              d.sugestions = result.data;
              d.showNoResultMessage = !hasAutocompleteSuggestions(result.data);
            }),
          );
        }

        setIsLoadingState(
          produce(d => {
            d.sugestionsLoading = false;
          }),
        );

        setSearchState(
          produce(d => {
            d.showSearchValue = searchState.debounceSearchValue;
          }),
        );

        const now = Date.now();
        if (
          !isLoadingState.sugestionsLoading &&
          hasAutocompleteSuggestions(result.data) &&
          now - lastSuggestionsSentTime > 1000 &&
          globalThis.pa
        ) {
          globalThis.pa.sendEvent('internal_search_result.suggestions', {
            ise_keyword: searchState.debounceSearchValue,
            product_names: result.data.products.map(item => item.productName),
            customer_group_id: customerInfo?.customerGroupId,
          });

          lastSuggestionsSentTime = now;
        }
      })();
    },
    config.debounceDelay,
  );

  const canSendDeleteEvent = useRef(true);
  const trackDeleteTracking = useCallback((value: string, count: number) => {
    if (canSendDeleteEvent.current && globalThis.pa) {
      globalThis.pa.sendEvent('internal_search_result.delete', {
        ise_keyword: value,
        ise_result: count,
        customer_group_id: customerInfo?.customerGroupId,
      });
      canSendDeleteEvent.current = false;
    }
  }, []);

  const isAutocompleteData = (
    newInputValue: string,
    prevInput: string,
    sugestions: AutocompleteResponseModel | null,
    clipboardValue?: string,
  ): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const suggestionTitles = [
      ...(sugestions?.brands.map(x => x.brandName) ?? []),
      ...(sugestions?.products.map(x => x.productName) ?? []),
      ...(sugestions?.categories.map(x => x.categoryName) ?? []),
    ];

    return (
      newInputValue.length - prevInput.length > 1 &&
      emailRegex.test(newInputValue) &&
      (!suggestionTitles.includes(newInputValue) ||
        (!!clipboardValue && !newInputValue.includes(clipboardValue)))
    );
  };

  const handleSearchChange = (e: any) => {
    if (!searchState.sugestions) {
      setSearchState(
        produce(d => {
          d.sugestions = null;
        }),
      );
    }

    const newValue = e.target.value as string;

    if (newValue.length < 3) {
      setSearchState(
        produce(d => {
          d.sugestions = null;
          d.debounceSearchValue = '';
        }),
      );
    }

    if (
      !(
        newValue.startsWith(searchState.searchValue) ||
        searchState.searchValue.startsWith(e.target.value)
      )
    ) {
      setSearchState(
        produce(d => {
          d.sugestions = null;
        }),
      );
    }

    if (
      isAutocompleteData(
        newValue,
        searchState.searchValue,
        searchState.sugestions,
        clipboardValueRef.current,
      )
    ) {
      return;
    }
    setSearchState(
      produce(d => {
        d.searchValue = newValue;
      }),
    );
  };

  const handleOnPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData('text');
    clipboardValueRef.current = pastedValue;
    setSearchState(
      produce(d => {
        d.searchValue = pastedValue;
      }),
    );
  };

  const handleSearch = (searchTerm: string, langChanged: boolean = false) => {
    if (searchTerm.length < 3) return;

    setIsLoadingState(
      produce(d => {
        d.isLoading = true;
      }),
    );

    const abortController = new AbortController();
    (async () => {
      const result = await searchProductService.getSearchProducts(
        encodeURIComponent(searchTerm),
        {
          signal: abortController?.signal,
        },
      );

      if (!result.hasErrors) {
        setSearchState(
          produce(d => {
            d.areSuggestionActive = false;
          }),
        );
        if (result.data.length > 0) {
          navigate(
            `/products/search?searchTerm=${encodeURIComponent(searchTerm)}`,
            {
              replace: true,
              state: {
                products: result.data,
                srchTrm: searchTerm,
              },
            },
          );
          if (!langChanged && globalThis.pa) {
            globalThis.pa.sendEvent('internal_search_result.display', {
              ise_keyword: searchTerm,
              ise_page: 1,
              customer_group_id: customerInfo?.customerGroupId,
            });
          }
        } else {
          setTimeout(() => {
            navigate(
              `/products/no-result?searchTerm=${encodeURIComponent(
                searchTerm,
              )}`,
              {
                replace: true,
                state: {
                  suggestions: suggestionsRef.current,
                },
              },
            );
            if (!langChanged && globalThis.pa) {
              globalThis.pa.sendEvent('internal_search_result.display', {
                ise_keyword: searchTerm,
                ise_page: 0,
                customer_group_id: customerInfo?.customerGroupId,
              });
            }
          }, config.debounceDelay);
        }
      } else {
        navigate('/products');
      }
    })();

    setIsLoadingState(
      produce(d => {
        d.isLoading = false;
      }),
    );
  };

  const handleKeyDownSearch = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch(event.currentTarget.value);
      event.currentTarget.blur();
    }

    if (event.key === 'ArrowDown' && !isTablet) {
      event.preventDefault();
      autocompleteRef.current?.focus({ preventScroll: true });
    }

    const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';
    if (isDeleteKey) {
      trackDeleteTracking(
        event.currentTarget.value,
        getAutocompleteSuggestionsCount(searchState.sugestions),
      );
    } else if (event.key.length === 1 || event.key === 'Enter') {
      canSendDeleteEvent.current = true;
    }
  };

  const handleClickSearch = () => {
    handleSearch(searchState.searchValue);
  };

  const handleSuggestionClick = (
    suggestion: AutocompleteSuggestionItem,
    index: number,
  ) => {
    const title = getAutocompleteSuggestionTitle(suggestion);

    setSearchState(
      produce(d => {
        d.searchValue = '';
        d.areSuggestionActive = false;
      }),
    );

    const target = getAutocompleteSuggestionNavigationTarget(suggestion);
    navigate(target.to, { state: target.state });

    if (globalThis.pa) {
      globalThis.pa.sendEvent('internal_search_result.click', {
        ise_keyword: searchState.searchValue,
        ise_click_rank: index,
        ise_page: 0,
        autocompletion: true,
        product_name: title,
        customer_group_id: customerInfo?.customerGroupId,
      });
    }
  };

  const showCancel = () =>
    !!searchState.searchValue && searchState.areSuggestionActive;

  useEffect(() => {
    if (
      searchState.sugestions &&
      hasAutocompleteSuggestions(searchState.sugestions)
    ) {
      suggestionsRef.current = searchState.sugestions;
    }

    if (!searchState.sugestions) suggestionsRef.current = undefined;
  }, [searchState.sugestions]);

  useEffect(() => {
    if (!searchState.debounceSearchValue)
      setSearchState(
        produce(d => {
          d.sugestions = null;
        }),
      );
  }, [searchState.debounceSearchValue]);

  useEffect(() => {
    if (searchState.debounceSearchValue.length > 2) {
      const abortController = new AbortController();
      debounceSearchChange(abortController);

      return () => {
        if (
          searchState.sugestions &&
          hasAutocompleteSuggestions(searchState.sugestions)
        )
          abortController.abort();
      };
    }
  }, [searchState.debounceSearchValue]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchState(
        produce(d => {
          d.debounceSearchValue = d.searchValue;
        }),
      );
    }, config.debounceDelay);
    return () => clearTimeout(timeoutId);
  }, [searchState.searchValue]);

  useEffect(() => {
    if (searchState.searchValue.length >= 3) {
      handleSearch(searchState.searchValue, true);
    }
  }, [customerInfo?.languageCode]);

  const getSuggestionsContainerClass = (): string => {
    if (!searchState.areSuggestionActive) {
      return '';
    }
    if (isLoadingState.sugestionsLoading) {
      return 'active-suggestions';
    }

    if (searchState.sugestions === null) {
      return 'active-no-suggestions';
    }

    return hasAutocompleteSuggestions(searchState.sugestions)
      ? 'active-suggestions'
      : 'active-no-suggestions';
  };

  return (
    <>
      {!isTablet ? (
        <div
          className={`app-bar-search sugestions-container ${getSuggestionsContainerClass()}`}
        >
          <ClickOutside
            className="search-outside-click"
            onClickOutside={() =>
              setSearchState(
                produce(d => {
                  d.areSuggestionActive = false;
                }),
              )
            }
          >
            <form onSubmit={e => e.preventDefault()} autoComplete="off">
              <input
                autoComplete="off"
                value={searchState.searchValue}
                disabled={isLoadingState.isLoading}
                onChange={handleSearchChange}
                onPaste={handleOnPaste}
                onKeyDown={handleKeyDownSearch}
                placeholder={translate(resources.searchPlaceholder)}
                onFocus={() =>
                  setSearchState(
                    produce(d => {
                      d.areSuggestionActive = true;
                    }),
                  )
                }
              />
              {showCancel() && (
                <Button
                  className="header-search-btn close"
                  onClick={() =>
                    setSearchState(
                      produce(d => {
                        d.searchValue = '';
                      }),
                    )
                  }
                >
                  <Icons.CloseSquare />
                </Button>
              )}
              <Button className="header-search-btn" onClick={handleClickSearch}>
                <Icons.SearchNormal />
              </Button>
              {searchState.areSuggestionActive && (
                <SearchSugestions
                  suggestions={searchState.sugestions}
                  searchValue={searchState.showSearchValue}
                  isLoading={isLoadingState.sugestionsLoading}
                  sugestionsRef={autocompleteRef}
                  onClick={handleSuggestionClick}
                />
              )}
            </form>
          </ClickOutside>
        </div>
      ) : (
        <>
          <div
            className={`app-bar-search mobile ${
              !!searchState.sugestions && searchState.areSuggestionActive
                ? 'active-sugestions'
                : ''
            }`}
          >
            <Button
              className="header-search-btn left"
              onClick={() => navigate(-1)}
            >
              <Icons.ArrowLeftMobile />
            </Button>
            <input
              autoComplete="off"
              type="text"
              value={searchState.searchValue}
              disabled={isLoadingState.isLoading}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDownSearch}
              placeholder={translate(resources.searchPlaceholder)}
              onFocus={() =>
                setSearchState(
                  produce(d => {
                    d.areSuggestionActive = true;
                  }),
                )
              }
            />
            {showCancel() && (
              <Button
                className="header-search-btn close"
                onClick={() =>
                  setSearchState(
                    produce(d => {
                      d.searchValue = '';
                    }),
                  )
                }
              >
                <Icons.CloseDelete />
              </Button>
            )}
            <Button className="header-search-btn" onClick={handleClickSearch}>
              <Icons.Search />
            </Button>
          </div>
          {searchState.areSuggestionActive && (
            <SearchSugestions
              suggestions={searchState.sugestions as AutocompleteResponseModel}
              searchValue={searchState.showSearchValue}
              isLoading={isLoadingState.sugestionsLoading}
              sugestionsRef={autocompleteRef}
              onClick={handleSuggestionClick}
            />
          )}
        </>
      )}
    </>
  );
};

export default Search;
